import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
// ─── PerformanceConfiguration.tsx ────────────────────────────────────────────
import { EditAction } from "@/components/table/actions";
import { format as formatDate } from "date-fns";
import { startCase } from "lodash";
import { Wrench } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { statusToVariant } from "@/components/ui/badge/statusToVariant";
import { Button } from "@/components/ui/button";
import { Table, type ColumnDef } from "@/components/table";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { PerformanceConfigCard } from "./configuration-card";
import { useGetPerformanceConfigurationQuery } from "@/services/(company)/api.performance";
import { PATHS } from "@/routes/paths";
import type { IPerformanceConfiguration } from "@/types/(company)/performance.interface";
import { ENUM_PERFORMANCE_CYCLE_TYPE } from "@/utils/enums/performance.enum";

type ConfigStatus = "active" | "inactive" | "not_configured";

const NOT_CONFIGURED: ConfigStatus = "not_configured";

const getStatus = (config: IPerformanceConfiguration): ConfigStatus => {
  if (!config.startDate && !config.endDate) return "not_configured";
  if (config.isActive) return "active";
  return "inactive";
};

const buildColumns = (t: TFunction): ColumnDef<Record<string, unknown>>[] => [
  { key: "teamName", label: t("company:performance.col_team_name") },
  {
    key: "selectedMetrics",
    label: t("company:performance.col_selected_metrics"),
  },
  { key: "cycleType", label: t("company:performance.col_cycle_type") },
  { key: "startDate", label: t("company:performance.col_start_date") },
  { key: "endDate", label: t("company:performance.col_end_date") },
  { key: "updatedDate", label: t("company:performance.col_updated_date") },
  { key: "status", label: t("company:performance.col_status") },
  { key: "actions", label: t("company:performance.col_actions") },
];

const buildDefaultCycleCards = (t: TFunction) => [
  {
    cycleType: ENUM_PERFORMANCE_CYCLE_TYPE.WEEKLY,
    name: t("company:performance.cycle_weekly"),
  },
  {
    cycleType: ENUM_PERFORMANCE_CYCLE_TYPE.MONTHLY,
    name: t("company:performance.cycle_monthly"),
  },
  {
    cycleType: ENUM_PERFORMANCE_CYCLE_TYPE.QUARTERLY,
    name: t("company:performance.cycle_quarterly"),
  },
  {
    cycleType: ENUM_PERFORMANCE_CYCLE_TYPE.ANNUALLY,
    name: t("company:performance.cycle_annually"),
  },
];

export const PerformanceConfiguration = () => {
  const { t } = useTranslation(["common", "company"]);
  const columns = buildColumns(t);
  const DEFAULT_CYCLE_CARDS = buildDefaultCycleCards(t);
  const navigate = useNavigate();
  const { id: teamId } = useParams<{ id: string }>();
  const { can } = usePermissions();
  const [page, setPage] = useState(1);

  const { data, isFetching } = useGetPerformanceConfigurationQuery(
    { teamId: teamId! },
    { skip: !teamId },
  );

  const configurations = data?.data ?? [];

  const cardData = DEFAULT_CYCLE_CARDS.map((defaultCard) => {
    const found = configurations.find(
      (c) => c.cycleType === defaultCard.cycleType,
    );
    return (
      found ?? {
        ...defaultCard,
        id: null,
        startDate: null,
        endDate: null,
        isActive: false,
      }
    );
  });

  const handleEdit = (configId: string | number, cycleType: string) => {
    navigate(
      `${PATHS.COMPANY.COMPANY_PERFORMANCE_PATHS.PERFORMANCE_CONFIGURATION_EDIT(teamId!, configId)}?cycleType=${cycleType}`,
    );
  };

  const handleConfigure = (cycleType: string) => {
    navigate(
      `${PATHS.COMPANY.COMPANY_PERFORMANCE_PATHS.PERFORMANCE_CONFIGURATION_CREATE(teamId!)}?cycleType=${cycleType}`,
    );
  };

  const rows = configurations.map((item: IPerformanceConfiguration) => {
    const status = getStatus(item);
    return {
      teamName: (
        <span className="text-sm text-gray-800">{item?.name ?? "-"}</span>
      ),
      selectedMetrics: (
        <span className="text-sm text-gray-600">
          {item?.metricsCount ?? "-"}
        </span>
      ),
      cycleType: (
        <span className="text-sm text-gray-600">
          {startCase(item.cycleType)}
        </span>
      ),
      startDate: (
        <span className="text-sm text-gray-600">
          {item.startDate ? formatDate(item.startDate, "dd MMMM, yyyy") : "-"}
        </span>
      ),
      endDate: (
        <span className="text-sm text-gray-600">
          {item.endDate ? formatDate(item.endDate, "dd MMMM, yyyy") : "-"}
        </span>
      ),
      updatedDate: (
        <span className="text-sm text-gray-600">
          {item.updatedAt ? formatDate(item.updatedAt, "MMM dd, yyyy") : "-"}
        </span>
      ),
      status: (
        <Badge variant={statusToVariant(status)} dot={false}>
          {startCase(status)}
        </Badge>
      ),
      actions: can(
        PLATFORM_SECTION_ENUM.PERFORMANCE,
        status === "not_configured" ? "create" : "edit",
      ) ? (
        status === "not_configured" ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={t("common:a11y.configure_cycle")}
            data-tooltip-id="hrm-tooltip"
            data-tooltip-content={t("common:a11y.configure_cycle")}
            className="text-orange-400 hover:text-orange-500"
            onClick={() => handleConfigure(item.cycleType)}
          >
            <Wrench size={16} />
          </Button>
        ) : (
          <EditAction
            tooltip={t("company:performance.edit_action")}
            onClick={() => handleEdit(item.id, item.cycleType)}
          />
        )
      ) : null,
    };
  });

  return (
    <div className="flex flex-col">
      {/* Cards — own padding */}
      <div className="shrink-0 p-6 border-b border-gray-100">
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          {t("company:performance.performance_configuration_heading")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {isFetching
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="h-56 bg-gray-100 animate-pulse rounded-xl"
                />
              ))
            : cardData.map((config, i) => {
                const status = config.id
                  ? getStatus(config as IPerformanceConfiguration)
                  : NOT_CONFIGURED;
                return (
                  <PerformanceConfigCard
                    key={config.cycleType ?? i}
                    title={config.name}
                    description={t("company:performance.configure_cycle_desc", {
                      cycle: startCase(config.cycleType),
                    })}
                    startDate={config.startDate ?? null}
                    endDate={config.endDate ?? null}
                    status={status}
                    onEdit={
                      can(PLATFORM_SECTION_ENUM.PERFORMANCE, "edit")
                        ? () => handleEdit(config.id ?? "", config.cycleType)
                        : undefined
                    }
                    onConfigure={
                      can(PLATFORM_SECTION_ENUM.PERFORMANCE, "create")
                        ? () => handleConfigure(config.cycleType)
                        : undefined
                    }
                  />
                );
              })}
        </div>
      </div>

      {/* Table — flush, heading padded to match Table's internal px-5 */}
      <h2 className="shrink-0 text-base font-semibold text-gray-900 px-5 pt-5 pb-3">
        {t("company:performance.configured_cycles_heading")}
      </h2>
      <Table
        columns={columns}
        data={rows}
        pagination={{
          totalPages: data?.meta?.totalPages ?? 1,
          currentPage: page,
          onPageChange: setPage,
        }}
        loading={isFetching}
      />
    </div>
  );
};
